import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import { Button, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import setorService from "../../../../services/SetorService";
import DynamicMaterialTable from "components/Common/MaterialTableContainer";
import useTableZoom from "components/Common/useTableZoom";
import Loading from "components/Common/Loading";
import { IconButton, Tooltip } from "@mui/material";
import { PencilSimple, Trash } from "@phosphor-icons/react";
import loginQuotesService from "services/LoginQuotesService";
import CreateEditLoginQuoteModal from "./CreateEditLoginQuoteModal";
import Swal from "sweetalert2";
import DeleteLoginQuoteModal from "./DeleteLoginQuoteModal";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import classnames from "classnames";
import loginImagesService from "services/LoginImagesService";
import UploadLoginImageModal from "./UploadLoginImageModal";
import DeleteLoginImageModal from "./DeleteLoginImageModal";
import {
	getTableBodyCellProps,
	getTableHeaderCellProps,
} from "components/Common/MaterialTableStyles";

const LoginQuotes = () => {
	// ========== CONFIGURAÇÃO DE LARGURAS PARA TABELA DE FRASES ==========
	const QUOTES_COLUMN_WIDTHS = {
		quote: { min: 200 },              // Largura mínima da coluna "Frase" (sem máximo - cresce livremente)
		author: { min: 150 },             // Largura mínima da coluna "Autor" (sem máximo - cresce livremente)
		actions: { min: 100, max: 100 },  // Largura mín/máx da coluna "Ações" (em pixels)
	};

	// ========== CONFIGURAÇÃO DE LARGURAS PARA TABELA DE IMAGENS ==========
	const IMAGES_COLUMN_WIDTHS = {
		name: { min: 300 },               // Largura mínima da coluna "Imagens" (sem máximo - cresce livremente)
		actions: { min: 80, max: 80 },    // Largura mín/máx da coluna "Ação" (em pixels)
	};
	// ================================================================

	const [data, setData] = useState([]);
	const [imagesData, setImagesData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [loginQuotesOpenModal, setLoginQuotesOpenModal] = useState(false);
	const [deleteLoginQuotesOpenModal, setDeleteLoginQuotesOpenModal] =
		useState(false);
	const [uploadLoginImageOpenModal, setUploadLoginImageOpenModal] =
		useState(false);
	const [dataLoginQuotesModal, setDataLoginQuotesModal] = useState(null);
	const [dataLoginImageModal, setDataLoginImageModal] = useState(null);
	const [deleteLoginImageOpenModal, setDeleteLoginImageOpenModal] =
		useState(false);
	const [activeTab, setActiveTab] = useState("frases");

	// Usar o hook customizado para zoom e cálculos de largura para ambas as tabelas
	const quotesZoom = useTableZoom(QUOTES_COLUMN_WIDTHS);
	const imagesZoom = useTableZoom(IMAGES_COLUMN_WIDTHS);

	const toggle = (tab) => {
		if (activeTab !== tab) setActiveTab(tab);
	};

	document.title = "Senda | Frases do Login";

	const getItems = async () => {
		try {
			setIsLoading(true);
			const response = await loginQuotesService.get();
			setData(response);
			const imageResponse = await loginImagesService.get();
			setImagesData(imageResponse);
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			console.log(error);
		}
	};

	useEffect(() => {
		getItems();
	}, []);

	const handleCreateEditLoginQuotes = (rowData) => {
		setDataLoginQuotesModal(rowData);
		setLoginQuotesOpenModal(true);
	};

	const handleUploadLoginImageModal = () => {
		setUploadLoginImageOpenModal(true);
	};

	const handleCloseCreateEditLoginQuotes = () => {
		setLoginQuotesOpenModal(false);
		getItems();
	};

	const handleCloseUploadLoginImageModal = () => {
		setUploadLoginImageOpenModal(false);
		getItems();
	};

	const handleDeleteLoginQuotesOpenModal = (rowData) => {
		setDataLoginQuotesModal(rowData);
		setDeleteLoginQuotesOpenModal(true);
	};

	const handleDeleteLoginImageModal = (rowData) => {
		setDataLoginImageModal(rowData);
		setDeleteLoginImageOpenModal(true);
	};

	const handleCloseDeleteLoginQuotes = () => {
		setDeleteLoginQuotesOpenModal(false);
		getItems();
	};

	const handleCloseDeleteLoginImages = () => {
		setDeleteLoginImageOpenModal(false);
		getItems();
	};

	const handleDeleteLoginQuote = async (id) => {
		try {
			setIsLoading(true);
			const response = await loginQuotesService.delete(id);
			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: response.message,
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			await SendaCustomSwal.fire({
				title: "Erro",
				text: error.message,
				iconHtml: '<i class="error-icon"></i>',
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		}
		handleCloseDeleteLoginQuotes();
	};

	const handleDeleteLoginImage = async (id) => {
		try {
			setIsLoading(true);
			const response = await loginImagesService.delete(id);
			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: response.message,
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			await SendaCustomSwal.fire({
				title: "Erro",
				text: error.message,
				iconHtml: '<i class="error-icon"></i>',
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		}
		handleCloseDeleteLoginImages();
	};

	const columns = useMemo(
		() => [
			{
				header: "Frase",
				accessorKey: "quote",
				filterFn: "contains",
				widthPixels: quotesZoom.getColumnWidth('quote'),
				minWidthPixels: QUOTES_COLUMN_WIDTHS.quote.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumn"),
			},
			{
				header: "Autor",
				accessorKey: "author",
				filterFn: "contains",
				widthPixels: quotesZoom.getColumnWidth('author'),
				minWidthPixels: QUOTES_COLUMN_WIDTHS.author.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
			},
			{
				header: <span>Ações</span>,
				accessorKey: "actions",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: quotesZoom.getColumnWidth('actions'),
				minWidthPixels: QUOTES_COLUMN_WIDTHS.actions.min,
				maxWidthPixels: QUOTES_COLUMN_WIDTHS.actions.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => (
					<div style={{ 
						display: "flex", 
						flexWrap: "wrap",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						height: "100%",
						gap: "2px",
					}}>
						<Tooltip title="Editar" placement="top">
							<IconButton
								id={`editar-${row.original.id}`}
								style={{
									background: "none",
									border: "none",
									padding: "1px",
									cursor: "pointer",
									color: "black",
									minWidth: "auto",
									width: "auto",
									height: "auto",
								}}
								onClick={() =>
									handleCreateEditLoginQuotes(
										row.original,
									)
								}
							>
								<PencilSimple size={14} />
							</IconButton>
						</Tooltip>
						<Tooltip title="Excluir" placement="top">
							<IconButton
								style={{
									background: "none",
									border: "none",
									padding: "1px",
									cursor: "pointer",
									color: "black",
									minWidth: "auto",
									width: "auto",
									height: "auto",
								}}
								onClick={() =>
									handleDeleteLoginQuotesOpenModal(
										row.original,
									)
								}
							>
								<Trash size={14} />
							</IconButton>
						</Tooltip>
					</div>
				),
				align: "center",
			},
		],
		[quotesZoom.zoomLevel], // Adicionado zoomLevel como dependência
	);

	const columnsImages = useMemo(
		() => [
			{
				header: "Imagens",
				accessorKey: "name",
				filterFn: "contains",
				widthPixels: imagesZoom.getColumnWidth('name'),
				minWidthPixels: IMAGES_COLUMN_WIDTHS.name.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumn"),
				enableSorting: false,
				enableColumnFilter: false,
				enableColumnActions: false,
				Cell: ({ row }) => (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: "100%",
							height: "100%",
						}}
					>
						<img
							src={row.original.name}
							style={{
								width: 250,
								height: 250,
								objectFit: "cover",
								borderRadius: 5,
							}}
						/>
					</div>
				),
			},
			{
				header: <span>Ação</span>,
				accessorKey: "actions",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: imagesZoom.getColumnWidth('actions'),
				minWidthPixels: IMAGES_COLUMN_WIDTHS.actions.min,
				maxWidthPixels: IMAGES_COLUMN_WIDTHS.actions.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => (
					<div style={{ 
						display: "flex", 
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						height: "100%",
					}}>
						<Tooltip title="Excluir" placement="top">
							<IconButton
								style={{
									background: "none",
									border: "none",
									padding: "1px",
									cursor: "pointer",
									color: "black",
									minWidth: "auto",
									width: "auto",
									height: "auto",
								}}
								onClick={() =>
									handleDeleteLoginImageModal(
										row.original,
									)
								}
							>
								<Trash size={14} />
							</IconButton>
						</Tooltip>
					</div>
				),
				align: "center",
			},
		],
		[imagesZoom.zoomLevel], // Adicionado zoomLevel como dependência
	);

	return (
		<Loading isLoading={isLoading}>
			<div className="page-content">
				{activeTab === "frases" && (
					<Breadcrumbs
						onBotaoClick={() => handleCreateEditLoginQuotes({})}
						botao={"Nova frase"}
						title="Frases do Login"
					/>
				)}

				{activeTab === "imagens" && (
					<Breadcrumbs
						onBotaoClick={() => handleUploadLoginImageModal()}
						botao={"Nova imagem"}
						title="Frases do Login"
					/>
				)}

				<div
					className="container-fluid"
					style={{
						backgroundColor: "white",
						padding: 20,
						borderRadius: 10,
						marginTop: 25,
					}}
				>
					<Nav tabs>
						<NavItem>
							<NavLink
								className={classnames({
									active: activeTab === "frases",
								})}
								onClick={() => {
									toggle("frases");
								}}
							>
								Frases
							</NavLink>
						</NavItem>
						<NavItem>
							<NavLink
								className={classnames({
									active: activeTab === "imagens",
								})}
								onClick={() => {
									toggle("imagens");
								}}
							>
								Imagens
							</NavLink>
						</NavItem>
					</Nav>
					<TabContent activeTab={activeTab}>
						<TabPane tabId="frases">
							<DynamicMaterialTable
								columns={columns}
								data={data}
							/>
						</TabPane>
						<TabPane tabId="imagens">
							<DynamicMaterialTable
								columns={columnsImages}
								data={imagesData}
							/>
						</TabPane>
					</TabContent>
				</div>
			</div>

			<CreateEditLoginQuoteModal
				show={loginQuotesOpenModal}
				onCloseClick={handleCloseCreateEditLoginQuotes}
				data={dataLoginQuotesModal}
			/>

			<UploadLoginImageModal
				show={uploadLoginImageOpenModal}
				onCloseClick={handleCloseUploadLoginImageModal}
			/>

			<DeleteLoginQuoteModal
				show={deleteLoginQuotesOpenModal}
				onCloseClick={handleCloseDeleteLoginQuotes}
				data={dataLoginQuotesModal}
				onDeleteClick={handleDeleteLoginQuote}
			/>

			<DeleteLoginImageModal
				show={deleteLoginImageOpenModal}
				onCloseClick={handleCloseDeleteLoginImages}
				data={dataLoginImageModal}
				onDeleteClick={handleDeleteLoginImage}
			/>
		</Loading>
	);
};
LoginQuotes.propTypes = {
	preGlobalFilteredRows: PropTypes.any,
};

export default withTranslation()(LoginQuotes);
